/**
 * City Planning Daily — mirrors iOS CityPlanningView Daily scroll layout.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import posthog from 'posthog-js';
import { useParams, useSearchParams } from 'react-router-dom';
import { Alert, Button, Input, Modal, Spin, TimePicker, message } from 'antd';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscriptionFeature } from '../../contexts/SubscriptionContext';
import {
  cityPlanningEn,
  formatDayChip,
  recommendedTimeBadge,
} from '../../content/cityPlanning.en';
import {
  formatItemTimeDisplay,
  itemTimeFromFormValue,
  itemTimeToDayjs,
} from '../../utils/itemTime';
import {
  getStoredPlanData,
  hydrateCityPlanFromFile,
} from '../../services/aiPlanPersistService';
import { pollCityPlanUntilReady } from '../../services/aiPlanStatusService';
import { startPlanJobs } from '../../services/aiPlanJobService';
import {
  fetchFirstPageAndCache,
  getCachedViatorProducts,
  type ViatorProduct,
} from '../../services/viatorProductsService';
import {
  getAccessibleTrip,
  updateTrip,
} from '../../services/tripsService';
import { loadLocalUserPreferences } from '../../services/userPreferencesSyncService';
import {
  isGooglePlacesConfigured,
  searchPlaces,
} from '../../services/googlePlacesService';
import { useCanonicalTripUrl } from '../../utils/useCanonicalTripUrl';
import type {
  AggregatedMustSeeItem,
  AggregatedPlanData,
  AggregatedPlanItem,
  PlanItineraryDayBlock,
  PlanTimeSlot,
} from '../../utils/aggregatedPlanData';
import { normalizePlanTitle } from '../../utils/aggregatedPlanData';
import {
  requiredSlots,
  slotLabel,
  remapStartHmIfOutsideSlot,
} from '../../utils/cityPlanningSlots';
import {
  preservedSpanMinutes,
  endHmFromStartAndDuration,
} from '../../utils/scheduleDurationSync';
import { buildSlotReviewItems, type SlotReviewItem } from '../../utils/cityPlanningViator';
import { isMealSlot } from '../../utils/slotSuggestionInterleaver';
import { isSlotBusy } from '../../utils/cityPlanningBusySlot';
import {
  stayDatesForCityVisit,
  stayArrivalDeparture,
  stayDurationDays,
  portTimesForStayDay,
} from '../../utils/cityPlanningStay';
import { portTimeLineForStayDay } from '../../utils/tripDayCards';
import {
  applyAddPlanItemAsOptional,
  applyAddPlanItemToTrip,
  applySaveDayBlocksWithStats,
  buildCityOptionFromPlan,
  buildPlanItineraryItem,
  buildUserPlanSelection,
  defaultTimesForSlot,
  hasScheduleOverlap,
  selectedNormalizedTitles,
  wallClockDateYmd,
  wallClockTimeHm,
} from '../../utils/planItemSave';
import {
  buildTourItineraryItem,
  type TourTimeSlot,
} from '../../utils/tourItemSave';
import { needsMealConfirmationPrompt } from '../../utils/reservationWarning';
import { effectiveCanEdit } from '../../utils/tripAccessPolicy';
import { captureSanitizedException, isPostHogConfigured } from '../../lib/posthog';
import type { Trip } from '../../types/client';
import TourDateSlotModal, {
  type TourDateSlotSelection,
} from '../../components/TourDateSlotModal/TourDateSlotModal';
import PlaceSearchModal, {
  type PlaceSearchCity,
  type PlaceSearchSelection,
  type PlaceSearchTourAdd,
} from '../../components/PlaceSearchModal/PlaceSearchModal';
import AddDayItemModal, {
  prefillFromPlace,
  type AddDayItemPrefill,
  type AddDayItemSavePayload,
} from '../../components/AddDayItemModal/AddDayItemModal';
import { CityPlanningChrome } from './cityPlanningShared';
import styles from './CityPlanning.module.css';

interface AddModalState {
  title: string;
  itemType: string;
  timeSlot: string;
  dateYmd: string;
  startTime: string;
  endTime: string;
  description?: string | null;
  location?: string | null;
  url?: string | null;
  reservationRequirement?: string | null;
  confirmationNumber?: string;
  geocode?: { latitude: number; longitude: number } | null;
}

function formatDurationMinutes(minutes: number | null | undefined): string {
  if (minutes == null || minutes <= 0) return '';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h > 0 && m > 0) return `${h}h ${m}m`;
  if (h > 0) return `${h}h`;
  return `${m}m`;
}

function hmToMins(hm: string | null | undefined): number | null {
  if (hm == null || !String(hm).trim()) return null;
  const parsed = wallClockTimeHm(String(hm));
  const match = /^(\d{1,2}):(\d{2})$/.exec(parsed);
  if (!match) return null;
  return Number(match[1]) * 60 + Number(match[2]);
}

function isHighlightOnTrip(
  trip: Trip,
  cityVisitId: string,
  item: AggregatedMustSeeItem
): boolean {
  const key = normalizePlanTitle(item.normalizedTitle || item.title);
  if (selectedNormalizedTitles(trip, cityVisitId).has(key)) return true;
  for (const day of trip.itineraryDays ?? []) {
    if (day.cityId && day.cityId !== cityVisitId) continue;
    for (const it of day.items ?? []) {
      if (normalizePlanTitle(it.title) === key) return true;
    }
  }
  return false;
}

function filterItineraryDays(
  plan: AggregatedPlanData,
  arrivalDate: string,
  departureDate: string,
  arrivalHm: string | null,
  departureHm: string | null
) {
  const days = plan.primaryItineraryDays ?? [];
  return days
    .filter((d) => {
      if (arrivalDate && d.date < arrivalDate) return false;
      if (departureDate && d.date > departureDate) return false;
      return true;
    })
    .map((day) => {
      let blocks = day.blocks ?? [];
      if (arrivalDate && day.date === arrivalDate && arrivalHm) {
        const arrMins = hmToMins(arrivalHm);
        if (arrMins != null) {
          blocks = blocks.filter((b) => {
            const start = hmToMins(b.startTime);
            return start == null || start >= arrMins;
          });
        }
      }
      if (departureDate && day.date === departureDate && departureHm) {
        const depMins = hmToMins(departureHm);
        if (depMins != null) {
          blocks = blocks.filter((b) => {
            const start = hmToMins(b.startTime);
            return start == null || start <= depMins;
          });
        }
      }
      return { ...day, blocks };
    });
}

function priorityLabel(priority: number | null | undefined): string | null {
  if (priority === 1) return 'Time: +++';
  if (priority === 2) return 'Time: ++';
  if (priority === 3) return 'Time: +';
  return null;
}

const CityPlanning: React.FC = () => {
  const { tripId } = useParams<{ tripId: string }>();
  const [searchParams] = useSearchParams();
  const cityVisitId = searchParams.get('cityVisitId')?.trim() ?? '';
  const { user } = useAuth();
  const includesAIPlan = useSubscriptionFeature('aiPlan');

  const [tripRevision, setTripRevision] = useState(0);
  const [loading, setLoading] = useState(true);
  const [polling, setPolling] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [plan, setPlan] = useState<AggregatedPlanData | null>(null);
  const [viatorProducts, setViatorProducts] = useState<ViatorProduct[]>([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [expandedMustSee, setExpandedMustSee] = useState(true);
  const [expandedItinerary, setExpandedItinerary] = useState(true);
  const [carouselIndex, setCarouselIndex] = useState<Record<string, number>>({});
  const [addOpen, setAddOpen] = useState(false);
  const [addModal, setAddModal] = useState<AddModalState | null>(null);
  const [tourPickerProduct, setTourPickerProduct] = useState<ViatorProduct | null>(null);
  const [tourPickerOpen, setTourPickerOpen] = useState(false);
  const [placeSearchOpen, setPlaceSearchOpen] = useState(false);
  const [addItemOpen, setAddItemOpen] = useState(false);
  const [addItemPrefill, setAddItemPrefill] = useState<AddDayItemPrefill | null>(null);

  const trip = useMemo(() => {
    if (!user?.sub || !tripId) return null;
    return getAccessibleTrip(user.sub, tripId);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- tripRevision forces reload after saves
  }, [user?.sub, tripId, tripRevision]);

  useCanonicalTripUrl(trip);

  const meta = cityVisitId ? trip?.cityVisitMeta?.[cityVisitId] : undefined;
  const cityName =
    (cityVisitId && trip?.cityNames?.[cityVisitId]) || cityPlanningEn.untitledCity;
  const canEdit = Boolean(trip && user?.sub && effectiveCanEdit(trip, user.sub));

  const cityDates = useMemo(() => {
    if (!trip || !cityVisitId) return [] as string[];
    return stayDatesForCityVisit(trip, cityVisitId);
  }, [trip, cityVisitId]);

  const { arrivalDate, departureDate } = useMemo(() => {
    if (!trip || !cityVisitId) return { arrivalDate: '', departureDate: '' };
    return stayArrivalDeparture(trip, cityVisitId);
  }, [trip, cityVisitId]);

  useEffect(() => {
    if (!selectedDate && cityDates.length > 0) {
      setSelectedDate(cityDates[0]);
    } else if (selectedDate && cityDates.length > 0 && !cityDates.includes(selectedDate)) {
      setSelectedDate(cityDates[0]);
    }
  }, [cityDates, selectedDate]);

  const dayPort = useMemo(() => {
    if (!trip || !cityVisitId || !selectedDate) {
      return {
        dayDate: selectedDate,
        hasArrival: false,
        arrivalHm: null as string | null,
        hasDeparture: false,
        departureHm: null as string | null,
      };
    }
    return portTimesForStayDay(trip, cityVisitId, selectedDate);
  }, [trip, cityVisitId, selectedDate]);

  const embarkDay = useMemo(() => {
    if (!trip?.cityIds) return null;
    for (const id of trip.cityIds) {
      if (trip.cityVisitMeta?.[id]?.isEmbarkation && trip.cityVisitMeta[id]?.arrivalDate) {
        return trip.cityVisitMeta[id].arrivalDate;
      }
    }
    return trip.cityVisitMeta?.[trip.cityIds[0] ?? '']?.arrivalDate ?? null;
  }, [trip]);

  const debarkDay = useMemo(() => {
    if (!trip?.cityIds?.length) return null;
    for (const id of trip.cityIds) {
      if (trip.cityVisitMeta?.[id]?.isDebarkation && trip.cityVisitMeta[id]?.arrivalDate) {
        return trip.cityVisitMeta[id].arrivalDate;
      }
    }
    const lastId = trip.cityIds[trip.cityIds.length - 1];
    return (
      trip.cityVisitMeta?.[lastId]?.departureDate ??
      trip.cityVisitMeta?.[lastId]?.arrivalDate ??
      null
    );
  }, [trip]);

  const visibleSlots = useMemo((): PlanTimeSlot[] => {
    if (!selectedDate) return ['morning', 'afternoon', 'evening'];
    return requiredSlots({
      dayDate: selectedDate,
      hasArrival: dayPort.hasArrival,
      arrivalHm: dayPort.arrivalHm,
      hasDeparture: dayPort.hasDeparture,
      departureHm: dayPort.departureHm,
      tripType: trip?.tripType,
      cruiseEmbarkDay: embarkDay,
      cruiseDebarkDay: debarkDay,
    });
  }, [selectedDate, dayPort, trip?.tripType, embarkDay, debarkDay]);

  const sightseeingStyle = useMemo(
    () => loadLocalUserPreferences().travelerPreferenceProfile.sightseeingStyle,
    [tripRevision]
  );

  const slotItems = useMemo(() => {
    if (!plan || !trip) {
      return {} as Record<PlanTimeSlot, SlotReviewItem[]>;
    }
    return buildSlotReviewItems({
      slotOptions: plan.slotOptions,
      visibleSlots,
      products: viatorProducts,
      selectedNormalizedTitles: selectedNormalizedTitles(trip, cityVisitId),
      sightseeingStyle,
      activityCatalog: plan.activityCatalog,
      mealCatalog: plan.mealCatalog,
    });
  }, [plan, trip, visibleSlots, viatorProducts, cityVisitId, sightseeingStyle]);

  const itineraryDays = useMemo(() => {
    if (!plan) return [];
    return filterItineraryDays(
      plan,
      arrivalDate,
      departureDate,
      trip?.cityVisitMeta?.[cityVisitId]?.portArrivalHm ?? null,
      trip?.cityVisitMeta?.[cityVisitId]?.portDepartureHm ?? null
    );
  }, [plan, arrivalDate, departureDate, trip, cityVisitId]);

  const dateOutsidePlan = useMemo(() => {
    if (!plan || !selectedDate) return false;
    if (plan.startDate && selectedDate < plan.startDate) return true;
    if (plan.endDate && selectedDate > plan.endDate) return true;
    return false;
  }, [plan, selectedDate]);

  const reloadPlan = useCallback(async (): Promise<AggregatedPlanData | null> => {
    if (!trip?.id || !cityVisitId) return null;
    const stored = getStoredPlanData(trip, cityVisitId);
    if (stored) return stored;
    return hydrateCityPlanFromFile(trip.id, cityVisitId);
  }, [trip, cityVisitId]);

  const refreshTrip = useCallback(() => {
    setTripRevision((n) => n + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const run = async (): Promise<void> => {
      if (!trip?.id || !cityVisitId || !user?.sub) {
        setLoading(false);
        return;
      }
      setLoading(true);
      let next = await reloadPlan();
      if (!next && meta?.aiJobId && includesAIPlan && meta.aiStatus !== 'failed') {
        setPolling(true);
        const outcome = await pollCityPlanUntilReady({
          tripId: trip.id,
          cityVisitId,
          aiJobId: meta.aiJobId,
        });
        setPolling(false);
        if (outcome.status === 'done') {
          refreshTrip();
          next = await reloadPlan();
        }
      }
      if (!cancelled) {
        setPlan(next);
        setLoading(false);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [
    trip?.id,
    cityVisitId,
    user?.sub,
    meta?.aiJobId,
    meta?.aiStatus,
    includesAIPlan,
    reloadPlan,
    refreshTrip,
  ]);

  useEffect(() => {
    if (!user?.sub || !cityVisitId) return;
    const cached = getCachedViatorProducts(user.sub, cityVisitId);
    if (cached.length > 0) {
      setViatorProducts(cached);
      return;
    }
    void fetchFirstPageAndCache(user.sub, cityVisitId)
      .then(setViatorProducts)
      .catch(() => undefined);
  }, [user?.sub, cityVisitId]);

  const persistTrip = useCallback(
    (updates: Parameters<typeof updateTrip>[2], successMessage?: string): boolean => {
      if (!user?.sub || !trip?.id) return false;
      const updated = updateTrip(user.sub, trip.id, updates);
      if (updated) {
        refreshTrip();
        if (successMessage) message.success(successMessage);
      }
      return Boolean(updated);
    },
    [user?.sub, trip?.id, refreshTrip]
  );

  const enrichWithPlaces = async (
    item: ReturnType<typeof buildPlanItineraryItem>
  ): Promise<ReturnType<typeof buildPlanItineraryItem>> => {
    if (item.location?.latitude != null && item.location?.longitude != null) {
      return item;
    }
    if (!isGooglePlacesConfigured()) return item;
    try {
      const results = await searchPlaces({
        textQuery: item.title,
        cityName,
        maxResults: 1,
      });
      const first = results[0];
      if (!first) return item;
      return {
        ...item,
        location: {
          address: first.address || item.location?.address || item.title,
          latitude: first.latitude,
          longitude: first.longitude,
        },
      };
    } catch {
      return item;
    }
  };

  const handleGeneratePlan = async (): Promise<void> => {
    if (!trip || !canEdit) {
      message.info(cityPlanningEn.viewerCannotSave);
      return;
    }
    setGenerating(true);
    try {
      startPlanJobs(trip, {
        cityVisitIds: [cityVisitId],
        clearExistingPlanData: true,
      });
      setPolling(true);
      const latestMeta = getAccessibleTrip(user!.sub, trip.id)?.cityVisitMeta?.[cityVisitId];
      const jobId = latestMeta?.aiJobId || meta?.aiJobId;
      if (jobId) {
        const outcome = await pollCityPlanUntilReady({
          tripId: trip.id,
          cityVisitId,
          aiJobId: jobId,
        });
        if (outcome.status === 'done') {
          refreshTrip();
          const next = await reloadPlan();
          setPlan(next);
        }
      } else {
        // Job may still be enqueueing — poll briefly via status on meta refresh
        for (let i = 0; i < 12; i += 1) {
          await new Promise((r) => setTimeout(r, 1500));
          refreshTrip();
          const t = getAccessibleTrip(user!.sub, trip.id);
          const jid = t?.cityVisitMeta?.[cityVisitId]?.aiJobId;
          if (jid) {
            const outcome = await pollCityPlanUntilReady({
              tripId: trip.id,
              cityVisitId,
              aiJobId: jid,
            });
            if (outcome.status === 'done') {
              const next = await reloadPlan();
              setPlan(next);
            }
            break;
          }
          const stored = t ? getStoredPlanData(t, cityVisitId) : null;
          if (stored) {
            setPlan(stored);
            break;
          }
        }
      }
    } finally {
      setPolling(false);
      setGenerating(false);
    }
  };

  const openAddPlanItem = (item: AggregatedPlanItem, dateYmd?: string): void => {
    const defaults = defaultTimesForSlot(item.timeSlot);
    const remapped = remapStartHmIfOutsideSlot(
      wallClockTimeHm(item.startTime || defaults.startTime),
      wallClockTimeHm(item.endTime || defaults.endTime),
      item.timeSlot as PlanTimeSlot
    );
    setAddModal({
      title: item.title,
      itemType: item.itemType,
      timeSlot: item.timeSlot,
      dateYmd: wallClockDateYmd(
        dateYmd || selectedDate || cityDates[0] || arrivalDate
      ),
      startTime: remapped.startTime,
      endTime: remapped.endTime,
      description: item.description,
      location: item.location,
      url: item.url,
      reservationRequirement: item.reservationRequirement,
      confirmationNumber: '',
      geocode: item.geocode,
    });
    setAddOpen(true);
  };

  const openAddBlock = (block: PlanItineraryDayBlock, dateYmd: string): void => {
    const defaults = defaultTimesForSlot((block.timeSlot as PlanTimeSlot) || 'morning');
    setAddModal({
      title: block.title || '',
      itemType: block.itemType || 'activity',
      timeSlot: block.timeSlot || 'morning',
      dateYmd: wallClockDateYmd(dateYmd),
      startTime: wallClockTimeHm(block.startTime || defaults.startTime),
      endTime: wallClockTimeHm(block.endTime || defaults.endTime),
      description: block.description,
      location: block.location,
      url: block.url,
      reservationRequirement: block.reservationRequirement,
      confirmationNumber: '',
    });
    setAddOpen(true);
  };

  const openAddViator = (product: ViatorProduct): void => {
    setTourPickerProduct(product);
    setTourPickerOpen(true);
  };

  const handleConfirmAdd = async (): Promise<void> => {
    if (!trip || !addModal || !user?.sub) return;
    if (!canEdit) {
      message.info(cityPlanningEn.viewerCannotSave);
      return;
    }
    const day = trip.itineraryDays?.find(
      (d) => d.date === addModal.dateYmd && (d.cityId === cityVisitId || !d.cityId)
    );
    if (hasScheduleOverlap(day?.items ?? [], addModal.startTime, addModal.endTime)) {
      message.warning(cityPlanningEn.overlapWarning);
    }

    let item = buildPlanItineraryItem({
      title: addModal.title,
      itemType: addModal.itemType,
      dateYmd: addModal.dateYmd,
      startTime: addModal.startTime,
      endTime: addModal.endTime,
      description: addModal.description,
      location: addModal.location,
      url: addModal.url,
      reservationRequirement: addModal.reservationRequirement,
      confirmationNumber: addModal.confirmationNumber?.trim() || undefined,
      geocode: addModal.geocode,
    });
    item = await enrichWithPlaces(item);

    const selection = buildUserPlanSelection({
      tripId: trip.id,
      cityVisitId,
      itemTitle: addModal.title,
      selectedSlot: addModal.timeSlot,
      selectedDate: addModal.dateYmd,
    });
    const didPersist = persistTrip(
      applyAddPlanItemToTrip(trip, {
        cityVisitId,
        dateYmd: addModal.dateYmd,
        item,
        selection,
      }),
      cityPlanningEn.itemAdded
    );
    if (didPersist && isPostHogConfigured) {
      posthog.capture('plan_item_added', {
        source: 'ai_plan',
        item_type: addModal.itemType,
        time_slot: addModal.timeSlot,
      });
    }
    setAddOpen(false);
    setAddModal(null);
  };

  const handleTourConfirm = async (selection: TourDateSlotSelection): Promise<void> => {
    if (!trip || !tourPickerProduct || !user?.sub || !canEdit) return;
    let item = buildTourItineraryItem({
      tour: tourPickerProduct,
      dateYmd: selection.dateYmd,
      slot: selection.slot,
      cityName,
    });
    item = await enrichWithPlaces(item);
    const sel = buildUserPlanSelection({
      tripId: trip.id,
      cityVisitId,
      itemTitle: tourPickerProduct.title,
      selectedSlot: selection.slot,
      selectedDate: selection.dateYmd,
    });
    const didPersist = persistTrip(
      applyAddPlanItemToTrip(trip, {
        cityVisitId,
        dateYmd: selection.dateYmd,
        item,
        selection: sel,
      }),
      cityPlanningEn.itemAdded
    );
    if (didPersist && isPostHogConfigured) {
      posthog.capture('plan_item_added', {
        source: 'viator',
        item_type: item.type,
        time_slot: selection.slot,
      });
    }
    setTourPickerOpen(false);
    setTourPickerProduct(null);
  };

  const handleAddOptional = (item: AggregatedPlanItem): void => {
    if (!trip || !canEdit) {
      message.info(cityPlanningEn.viewerCannotSave);
      return;
    }
    const existing = trip.cityOptions?.[cityVisitId] ?? [];
    const option = buildCityOptionFromPlan({
      title: item.title,
      description: item.description,
      url: item.url,
      address: item.location,
      order: existing.length,
    });
    const selection = buildUserPlanSelection({
      tripId: trip.id,
      cityVisitId,
      itemTitle: item.title,
      normalizedTitle: item.normalizedTitle,
      selectedSlot: item.timeSlot,
    });
    persistTrip(
      applyAddPlanItemAsOptional(trip, {
        cityVisitId,
        option,
        selection,
      }),
      cityPlanningEn.optionAdded
    );
  };

  const handleSaveDay = (dateYmd: string): void => {
    if (!trip || !plan?.primaryItineraryDays || !canEdit) {
      message.info(cityPlanningEn.viewerCannotSave);
      return;
    }
    const day = itineraryDays.find((d) => d.date === dateYmd);
    if (!day?.blocks?.length) return;
    const result = applySaveDayBlocksWithStats(trip, {
      cityVisitId,
      dateYmd,
      blocks: day.blocks,
    });
    persistTrip(result.patch);
    if (result.conflictCount > 0) {
      message.success(
        cityPlanningEn.daySavedSummary
          .replace('{saved}', String(result.savedCount))
          .replace('{conflicts}', String(result.conflictCount))
      );
    } else {
      message.success(
        cityPlanningEn.daySavedSimple.replace('{saved}', String(result.savedCount))
      );
    }
  };

  const handlePlaceSelected = (selection: PlaceSearchSelection): void => {
    setPlaceSearchOpen(false);
    setAddItemPrefill(prefillFromPlace(selection.place, selection.suggestedType));
    setAddItemOpen(true);
  };

  const handleManualEntry = (_city: PlaceSearchCity): void => {
    setPlaceSearchOpen(false);
    setAddItemPrefill({ type: 'activity' });
    setAddItemOpen(true);
  };

  const handleTourAddFromSearch = async (payload: PlaceSearchTourAdd): Promise<void> => {
    if (!trip || !user?.sub || !canEdit) return;
    let item = buildTourItineraryItem({
      tour: payload.tour,
      dateYmd: payload.dateYmd,
      slot: payload.slot as TourTimeSlot,
      cityName,
    });
    item = await enrichWithPlaces(item);
    const sel = buildUserPlanSelection({
      tripId: trip.id,
      cityVisitId,
      itemTitle: payload.tour.title,
      selectedSlot: payload.slot,
      selectedDate: payload.dateYmd,
    });
    persistTrip(
      applyAddPlanItemToTrip(trip, {
        cityVisitId,
        dateYmd: payload.dateYmd,
        item,
        selection: sel,
      }),
      cityPlanningEn.itemAdded
    );
    setPlaceSearchOpen(false);
  };

  const handleSavePlaceItem = (payload: AddDayItemSavePayload): void => {
    if (!trip || !canEdit) return;
    const dateYmd = selectedDate || cityDates[0] || arrivalDate;
    const selection = buildUserPlanSelection({
      tripId: trip.id,
      cityVisitId,
      itemTitle: payload.item.title,
      selectedDate: dateYmd,
    });
    persistTrip(
      applyAddPlanItemToTrip(trip, {
        cityVisitId,
        dateYmd,
        item: payload.item,
        selection,
      }),
      cityPlanningEn.itemAdded
    );
    setAddItemOpen(false);
    setAddItemPrefill(null);
  };

  if (!includesAIPlan) {
    return (
      <div className={styles.page}>
        <div className={styles.container}>
          <Alert type="info" message={cityPlanningEn.noSubscription} showIcon />
        </div>
      </div>
    );
  }

  if (!tripId || !cityVisitId) {
    return (
      <div className={styles.page}>
        <div className={styles.container}>
          <Alert type="warning" message={cityPlanningEn.missingCity} showIcon />
        </div>
      </div>
    );
  }

  if (!trip && !loading) {
    return (
      <div className={styles.page}>
        <div className={styles.container}>
          <Alert type="error" message={cityPlanningEn.missingTrip} showIcon />
        </div>
      </div>
    );
  }

  const aiStatus = meta?.aiStatus?.toLowerCase();
  const showQueued =
    polling ||
    generating ||
    aiStatus === 'queued' ||
    aiStatus === 'running' ||
    aiStatus === 'processing';
  const showFailed = aiStatus === 'failed' && !plan;
  const findingPlan = loading || (showQueued && !plan);
  const duration = stayDurationDays(arrivalDate, departureDate);
  const timeLine = portTimeLineForStayDay({
    dayYmd: selectedDate,
    arrivalDate,
    departureDate,
    portArrivalHm: trip?.cityVisitMeta?.[cityVisitId]?.portArrivalHm ?? null,
    portDepartureHm: trip?.cityVisitMeta?.[cityVisitId]?.portDepartureHm ?? null,
  });

  const searchCities: PlaceSearchCity[] = trip
    ? [
        {
          id: cityVisitId,
          cityName,
          countryName: meta?.countryName,
        },
      ]
    : [];

  const renderPlanCard = (
    entry: SlotReviewItem,
    slot: PlanTimeSlot,
    busy: boolean
  ): React.ReactNode => {
    if (entry.kind === 'viator') {
      const { product } = entry;
      const price =
        product.price?.startingPrice != null && product.price.startingPrice > 0
          ? `${product.price.currency} ${Math.round(product.price.startingPrice)}+`
          : null;
      return (
        <article key={product.productCode} className={styles.card}>
          <p className={styles.cardTitle}>{product.title}</p>
          <p className={styles.cardMeta}>
            {cityPlanningEn.tourSource}
            {price ? ` · ${price}` : ''}
          </p>
          {product.description ? (
            <p className={styles.cardDesc}>{product.description}</p>
          ) : null}
          <div className={styles.cardActions}>
            {product.productUrl ? (
              <Button
                size="small"
                href={product.productUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {cityPlanningEn.bookTour}
              </Button>
            ) : null}
            <Button
              size="small"
              type="primary"
              disabled={!canEdit || (busy && !isMealSlot(slot))}
              onClick={() => openAddViator(product)}
            >
              {cityPlanningEn.addToTrip}
            </Button>
          </div>
        </article>
      );
    }

    const item = entry.item;
    const scheduled = Boolean(entry.isScheduled);
    const meal = isMealSlot(slot);
    const durationMins =
      item.suggestedDurationMinutes ??
      (item.startTime && item.endTime
        ? (() => {
            const a = hmToMins(item.startTime);
            const b = hmToMins(item.endTime);
            return a != null && b != null && b > a ? b - a : null;
          })()
        : null);
    const timeLineText = meal
      ? [formatItemTimeDisplay(item.startTime), formatItemTimeDisplay(item.endTime)]
          .filter(Boolean)
          .join(' – ')
      : formatDurationMinutes(durationMins);
    const pri = priorityLabel(item.slotPriority);
    const dim = busy && !meal && !scheduled;

    return (
      <article
        key={item.id}
        className={`${styles.card} ${dim ? styles.cardDimmed : ''}`}
      >
        <div className={styles.cardTitleRow}>
          <p className={styles.cardTitle}>{item.title}</p>
          {scheduled ? (
            <span className={styles.scheduledBadge}>{cityPlanningEn.scheduled}</span>
          ) : null}
        </div>
        <p className={styles.cardMeta}>
          {cityPlanningEn.planSource}
          {timeLineText ? ` · ${timeLineText}` : ''}
          {item.priceHint ? ` · ${item.priceHint}` : ''}
        </p>
        {item.description ? <p className={styles.cardDesc}>{item.description}</p> : null}
        <div className={styles.cardBadges}>
          {item.operatingHours ? (
            <span className={styles.badge}>{item.operatingHours}</span>
          ) : null}
          {meal &&
          item.reservationRequirement &&
          item.reservationRequirement !== 'not_needed' ? (
            <span className={`${styles.badge} ${styles.badgeReservation}`}>
              {item.reservationRequirement}
            </span>
          ) : null}
          {pri ? (
            <span
              className={`${styles.badge} ${
                item.slotPriority === 1
                  ? styles.badgePriority1
                  : item.slotPriority === 2
                    ? styles.badgePriority2
                    : ''
              }`}
            >
              {pri}
            </span>
          ) : null}
          {item.location ? <span className={styles.badge}>{item.location}</span> : null}
        </div>
        {item.bestTimeReason && item.slotPriority === 1 ? (
          <p className={styles.bestTimeReason}>{item.bestTimeReason}</p>
        ) : null}
        {item.tags?.length ? (
          <div className={styles.tagRow}>
            {item.tags.slice(0, 8).map((tag) => (
              <span key={tag} className={styles.tagChip}>
                {tag}
              </span>
            ))}
          </div>
        ) : null}
        {!scheduled ? (
          <div className={styles.cardActions}>
            <Button
              type="primary"
              size="small"
              disabled={!canEdit || (busy && !meal)}
              onClick={() => openAddPlanItem(item)}
            >
              {cityPlanningEn.addToTrip}
            </Button>
            <Button
              size="small"
              disabled={!canEdit}
              onClick={() => handleAddOptional(item)}
            >
              {cityPlanningEn.addAsOptional}
            </Button>
          </div>
        ) : null}
      </article>
    );
  };

  return (
    <CityPlanningChrome
      trip={trip}
      userId={user?.sub}
      cityName={cityName}
      cityVisitId={cityVisitId}
      section="daily"
      canEdit={canEdit}
      onSearchPlaces={() => setPlaceSearchOpen(true)}
    >
      {!canEdit ? (
        <Alert
          type="info"
          message={cityPlanningEn.readOnly}
          showIcon
          className={styles.statusBanner}
        />
      ) : null}

      {findingPlan ? (
        <div className={styles.findingPlan}>
          <Spin size="large" />
          <div className={styles.findingPlanText}>
            <strong>
              {polling ||
              generating ||
              aiStatus === 'running' ||
              aiStatus === 'processing'
                ? cityPlanningEn.running
                : loading
                  ? cityPlanningEn.findingPlan
                  : cityPlanningEn.queued}
            </strong>
            {loading ? (
              <span className={styles.findingPlanSub}>
                {cityPlanningEn.findingPlanSubtitle}
              </span>
            ) : null}
          </div>
        </div>
      ) : null}

      {showFailed ? (
        <div className={styles.emptyPlan}>
          <Alert type="error" message={cityPlanningEn.failed} showIcon />
          {canEdit ? (
            <Button type="primary" loading={generating} onClick={() => void handleGeneratePlan()}>
              {cityPlanningEn.failedRetry}
            </Button>
          ) : null}
        </div>
      ) : null}

      {!findingPlan && !plan && !showFailed ? (
        <div className={styles.emptyPlan}>
          <h2>{cityPlanningEn.noPlanYet}</h2>
          <p>
            {cityPlanningEn.generatePlanBody.replace('{city}', cityName)}
          </p>
          {canEdit ? (
            <Button type="primary" loading={generating} onClick={() => void handleGeneratePlan()}>
              {cityPlanningEn.generatePlan}
            </Button>
          ) : (
            <p className={styles.emptySlot}>{cityPlanningEn.noPlanYetBody}</p>
          )}
        </div>
      ) : null}

      {!findingPlan && plan ? (
        <>
          <section className={styles.headerCard}>
            <h2>{cityPlanningEn.cityInfo}</h2>
            <p>
              {plan.summary?.trim() || cityPlanningEn.cityDescriptionFallback}
            </p>
            <div className={styles.headerMeta}>
              <div>
                {timeLine ? <div>{timeLine}</div> : null}
              </div>
              <div style={{ textAlign: 'right' }}>
                <div className={styles.headerMetaLabel}>{cityPlanningEn.duration}</div>
                <div>
                  {duration}{' '}
                  {duration === 1
                    ? cityPlanningEn.durationDay
                    : cityPlanningEn.durationDays}
                </div>
              </div>
            </div>
          </section>

          <section className={styles.sectionCard}>
            <button
              type="button"
              className={styles.sectionToggle}
              aria-expanded={expandedMustSee}
              onClick={() => setExpandedMustSee((v) => !v)}
            >
              {cityPlanningEn.highlights}
              <span className={styles.sectionToggleChevron}>
                {expandedMustSee ? '▲' : '▼'}
              </span>
            </button>
            {expandedMustSee ? (
              plan.mustSee.length === 0 ? (
                <p className={styles.emptySlot}>{cityPlanningEn.highlightsEmpty}</p>
              ) : (
                <div>
                  {plan.mustSee.map((m) => {
                    const checked = trip
                      ? isHighlightOnTrip(trip, cityVisitId, m)
                      : false;
                    return (
                      <div key={m.id} className={styles.highlightCard}>
                        <div className={styles.cardTitleRow}>
                          <strong
                            className={checked ? styles.cardTitleStruck : undefined}
                          >
                            {m.title}
                          </strong>
                          {checked ? (
                            <span className={styles.scheduledBadge}>✓</span>
                          ) : null}
                        </div>
                        {m.whyImportant ? (
                          <p className={styles.cardMeta}>{m.whyImportant}</p>
                        ) : null}
                        {m.recommendedTimes?.length ? (
                          <div className={styles.highlightTimes}>
                            {m.recommendedTimes.map((t, idx) => (
                              <span
                                key={`${m.id}-${t}`}
                                className={`${styles.highlightTime} ${
                                  idx === 0 ? styles.highlightTimePrimary : ''
                                }`}
                              >
                                {recommendedTimeBadge(t)}
                              </span>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              )
            ) : null}
          </section>

          <section className={styles.sectionCard}>
            <button
              type="button"
              className={styles.sectionToggle}
              aria-expanded={expandedItinerary}
              onClick={() => setExpandedItinerary((v) => !v)}
            >
              {cityPlanningEn.itineraryPlan}
              <span className={styles.sectionToggleChevron}>
                {expandedItinerary ? '▲' : '▼'}
              </span>
            </button>
            {expandedItinerary ? (
              itineraryDays.length === 0 ? (
                <p className={styles.emptySlot}>{cityPlanningEn.emptyItinerary}</p>
              ) : (
                itineraryDays.map((day) => (
                  <div key={day.date} className={styles.dayBlock}>
                    <div className={styles.dayHeader}>
                      <strong>
                        {cityName} — {formatDayChip(day.date)}
                      </strong>
                      <Button
                        type="primary"
                        size="small"
                        disabled={!canEdit || !day.blocks.length}
                        onClick={() => handleSaveDay(day.date)}
                      >
                        {cityPlanningEn.saveDay}
                      </Button>
                    </div>
                    {day.blocks.map((block, idx) => {
                      const dayItems =
                        trip?.itineraryDays?.find(
                          (d) =>
                            d.date === day.date &&
                            (d.cityId === cityVisitId || !d.cityId)
                        )?.items ?? [];
                      const start = block.startTime || '';
                      const end = block.endTime || start;
                      const conflict =
                        Boolean(start) &&
                        hasScheduleOverlap(dayItems, start, end || start);
                      return (
                        <div key={`${day.date}-${idx}`} className={styles.blockRow}>
                          <div>
                            <div>{block.title}</div>
                            <div className={styles.blockMeta}>
                              {block.startTime
                                ? formatItemTimeDisplay(block.startTime)
                                : ''}
                              {conflict ? (
                                <span className={styles.blockConflict}>
                                  {' '}
                                  · conflict
                                </span>
                              ) : null}
                            </div>
                          </div>
                          <Button
                            size="small"
                            disabled={!canEdit || conflict || !block.title}
                            onClick={() => openAddBlock(block, day.date)}
                          >
                            +
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                ))
              )
            ) : null}
          </section>

          <div
            className={styles.datePicker}
            role="listbox"
            aria-label={cityPlanningEn.pickDate}
          >
            {cityDates.map((ymd) => (
              <button
                key={ymd}
                type="button"
                role="option"
                aria-selected={ymd === selectedDate}
                className={
                  ymd === selectedDate ? styles.dayChipActive : styles.dayChip
                }
                onClick={() => {
                  setSelectedDate(ymd);
                  setCarouselIndex({});
                }}
              >
                {formatDayChip(ymd)}
              </button>
            ))}
          </div>

          {dateOutsidePlan ? (
            <p className={styles.emptySlot}>{cityPlanningEn.noPlanForDay}</p>
          ) : (
            visibleSlots.map((slot) => {
              const entries = slotItems[slot] ?? [];
              const busy = Boolean(
                trip &&
                  selectedDate &&
                  isSlotBusy({
                    slot,
                    dateYmd: selectedDate,
                    cityVisitId,
                    itineraryDays: trip.itineraryDays,
                  })
              );
              const idx = carouselIndex[slot] ?? 0;
              const clamped = Math.min(Math.max(idx, 0), Math.max(entries.length - 1, 0));
              const current = entries[clamped];

              return (
                <section
                  key={slot}
                  className={`${styles.slotSection} ${
                    isMealSlot(slot)
                      ? styles.slotSectionMeal
                      : styles.slotSectionActivity
                  }`}
                >
                  <div className={styles.slotTitleRow}>
                    <h2 className={styles.slotTitle}>{slotLabel(slot)}</h2>
                    {entries.length > 0 ? (
                      <div className={styles.slotNav}>
                        <button
                          type="button"
                          aria-label="Previous"
                          disabled={clamped <= 0}
                          onClick={() =>
                            setCarouselIndex((prev) => ({
                              ...prev,
                              [slot]: clamped - 1,
                            }))
                          }
                        >
                          <LeftOutlined />
                        </button>
                        <span>
                          {cityPlanningEn.slotOf
                            .replace('{current}', String(clamped + 1))
                            .replace('{total}', String(entries.length))}
                        </span>
                        <button
                          type="button"
                          aria-label="Next"
                          disabled={clamped >= entries.length - 1}
                          onClick={() =>
                            setCarouselIndex((prev) => ({
                              ...prev,
                              [slot]: clamped + 1,
                            }))
                          }
                        >
                          <RightOutlined />
                        </button>
                      </div>
                    ) : null}
                  </div>
                  {entries.length === 0 ? (
                    <p className={styles.emptySlot}>{cityPlanningEn.emptySlot}</p>
                  ) : current ? (
                    <div className={styles.carousel}>
                      <div className={styles.carouselCard}>
                        {renderPlanCard(current, slot, busy)}
                      </div>
                    </div>
                  ) : null}
                </section>
              );
            })
          )}
        </>
      ) : null}

      <Modal
        open={addOpen}
        title={cityPlanningEn.addToTrip}
        onCancel={() => setAddOpen(false)}
        onOk={() => void handleConfirmAdd()}
        okText={cityPlanningEn.confirmAdd}
        cancelText={cityPlanningEn.cancel}
      >
        {addModal ? (
          <div className={styles.modalFields}>
            <strong>{addModal.title}</strong>
            <label>{cityPlanningEn.pickDate}</label>
            <div className={styles.datePicker} role="listbox">
              {cityDates.map((ymd) => (
                <button
                  key={ymd}
                  type="button"
                  role="option"
                  aria-selected={ymd === addModal.dateYmd}
                  className={
                    ymd === addModal.dateYmd ? styles.dayChipActive : styles.dayChip
                  }
                  onClick={() => setAddModal({ ...addModal, dateYmd: ymd })}
                >
                  {formatDayChip(ymd)}
                </button>
              ))}
            </div>
            <label htmlFor="plan-time">{cityPlanningEn.pickTime}</label>
            <TimePicker
              id="plan-time"
              format="h:mm A"
              use12Hours
              value={itemTimeToDayjs(addModal.startTime)}
              onChange={(value) => {
                const startTime = itemTimeFromFormValue(value);
                if (!startTime) return;
                const span = preservedSpanMinutes(addModal.startTime, addModal.endTime);
                setAddModal({
                  ...addModal,
                  startTime,
                  endTime: endHmFromStartAndDuration(startTime, span),
                });
              }}
            />
            {needsMealConfirmationPrompt(
              addModal.itemType,
              addModal.reservationRequirement
            ) ? (
              <>
                <label htmlFor="plan-confirmation">
                  {cityPlanningEn.confirmationNumber}
                </label>
                <Input
                  id="plan-confirmation"
                  value={addModal.confirmationNumber ?? ''}
                  placeholder={cityPlanningEn.confirmationNumberHint}
                  onChange={(e) =>
                    setAddModal({
                      ...addModal,
                      confirmationNumber: e.target.value,
                    })
                  }
                />
                <p className={styles.confirmationHint}>
                  {cityPlanningEn.confirmationNumberHint}
                </p>
              </>
            ) : null}
          </div>
        ) : null}
      </Modal>

      <TourDateSlotModal
        open={tourPickerOpen}
        tour={tourPickerProduct}
        arrivalDate={arrivalDate}
        departureDate={departureDate}
        initialDate={selectedDate || arrivalDate}
        onCancel={() => {
          setTourPickerOpen(false);
          setTourPickerProduct(null);
        }}
        onConfirm={(sel) => void handleTourConfirm(sel)}
      />

      <PlaceSearchModal
        open={placeSearchOpen}
        cities={searchCities}
        initialCityId={cityVisitId}
        enableTours
        userId={user?.sub}
        arrivalDate={arrivalDate}
        departureDate={departureDate}
        initialTourDate={selectedDate}
        onClose={() => setPlaceSearchOpen(false)}
        onPlaceSelected={handlePlaceSelected}
        onManualEntry={handleManualEntry}
        onTourAdd={(p) => void handleTourAddFromSearch(p)}
      />

      <AddDayItemModal
        open={addItemOpen}
        prefill={addItemPrefill}
        onClose={() => {
          setAddItemOpen(false);
          setAddItemPrefill(null);
        }}
        onSave={handleSavePlaceItem}
      />
    </CityPlanningChrome>
  );
};

export default CityPlanning;
